import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Pageable } from '@app/shared/models/Pageable.interface';
import { Observable, Subscription, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type PaginationPageLoader<T = unknown> = (
  pageIndex: number,
  pageSize: number
) => Observable<Pageable<T>>;

@Component({
  selector: 'app-pagination',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './pagination.html',
  styleUrl: './pagination.scss'
})
export class PaginationComponent<T = unknown> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private activePageRequest?: Subscription;

  readonly section = input('node-joining');
  readonly position = input<'top' | 'bottom'>('top');
  readonly rows = input<readonly T[]>([]);
  readonly dataSource = input.required<MatTableDataSource<T>>();
  readonly pageLoader = input<PaginationPageLoader<T> | null>(null);
  readonly pageSizeOptions = input<number[]>([5, 10, 20]);
  readonly showLabel = input('Show:');
  readonly perPageLabel = input('per page');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  readonly length = signal(0);

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.length() / this.pageSize())));
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));

  constructor() {
    effect(() => {
      if (this.pageLoader()) {
        return;
      }

      const rows = this.rows();
      const pageSize = this.pageSize();
      const nextPageIndex = this.clampPageIndex(this.pageIndex(), rows.length, pageSize);

      this.length.set(rows.length);
      if (nextPageIndex !== this.pageIndex()) {
        this.pageIndex.set(nextPageIndex);
        return;
      }

      this.setClientPage(rows, nextPageIndex, pageSize);
    });
  }

  ngOnInit(): void {
    if (this.pageLoader()) {
      this.loadServerPage(0, this.pageSize());
    }
  }

  changePageSize(pageSize: string | number): void {
    const nextSize = Number(pageSize);
    if (!Number.isFinite(nextSize) || nextSize <= 0 || nextSize === this.pageSize()) {
      return;
    }

    this.setPage(0, nextSize);
  }

  goToPage(pageIndex: number): void {
    if (pageIndex < 0 || pageIndex >= this.totalPages() || pageIndex === this.pageIndex()) {
      return;
    }

    this.setPage(pageIndex, this.pageSize());
  }

  private setPage(pageIndex: number, pageSize: number): void {
    if (this.pageLoader()) {
      this.loadServerPage(pageIndex, pageSize);
      return;
    }

    this.pageSize.set(pageSize);
    this.pageIndex.set(this.clampPageIndex(pageIndex, this.rows().length, pageSize));
  }

  private loadServerPage(pageIndex: number, pageSize: number): void {
    const pageLoader = this.pageLoader();
    if (!pageLoader) {
      return;
    }

    this.activePageRequest?.unsubscribe();
    this.activePageRequest = pageLoader(pageIndex, pageSize)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((page) => {
        this.dataSource().data = page.content;
        this.pageIndex.set(page.number);
        this.pageSize.set(page.size);
        this.length.set(page.totalElements);
      });
  }

  private setClientPage(rows: readonly T[], pageIndex: number, pageSize: number): void {
    const start = pageIndex * pageSize;
    this.dataSource().data = [...rows.slice(start, start + pageSize)];
  }

  private clampPageIndex(pageIndex: number, length: number, pageSize: number): number {
    return Math.min(Math.max(pageIndex, 0), Math.max(0, Math.ceil(length / pageSize) - 1));
  }
}
